import React, { useState, useEffect, useLayoutEffect } from 'react';
import { pdfjs, Document, Page, PasswordResponses } from 'react-pdf';
import { type PDFDocumentProxy } from 'pdfjs-dist';
import ScrollContainer from 'react-indiana-drag-scroll';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const options = {
  cMapUrl: '/cmaps/',
  standardFontDataUrl: '/standard_fonts/',
};

const MIN_SCALE = 0.5;
const MAX_SCALE = 5;
const BUTTON_SCALE_STEP = 0.25;
const TOUCH_PAN_ACTIVATION_DISTANCE = 4;
const OVERSCROLL_LOCK_CLASS = 'pdf-viewer-overscroll-lock';

let overscrollLockCount = 0;

interface Props {
  url: string;
  maxWidth: number;
  handleException: Function;
}

type PointerPosition = {
  clientX: number;
  clientY: number;
};

type PinchState = {
  startDistance: number;
  startMidpointX: number;
  startMidpointY: number;
  startScale: number;
  startScrollLeft: number;
  startScrollTop: number;
  startContentWidth: number;
  startContentHeight: number;
};

type PendingScroll = {
  left: number;
  top: number;
};

type VisualPinchFrame = PendingScroll & {
  scaleRatio: number;
  displayScale: number;
};

type TouchPanState = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startScrollLeft: number;
  startScrollTop: number;
  hasMoved: boolean;
};

function clampScale(nextScale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
}

function getDistance(first: PointerPosition, second: PointerPosition): number {
  return Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
}

function getMidpoint(first: PointerPosition, second: PointerPosition): PointerPosition {
  return {
    clientX: (first.clientX + second.clientX) / 2,
    clientY: (first.clientY + second.clientY) / 2,
  };
}

function lockPageOverscroll() {
  const shouldLockNativeScroll = overscrollLockCount === 0;
  overscrollLockCount += 1;
  document.documentElement.classList.add(OVERSCROLL_LOCK_CLASS);
  document.body.classList.add(OVERSCROLL_LOCK_CLASS);

  if (shouldLockNativeScroll) {
    setNativeScrollEnabled(false);
  }
}

function unlockPageOverscroll() {
  overscrollLockCount = Math.max(0, overscrollLockCount - 1);

  if (overscrollLockCount > 0) {
    return;
  }

  document.documentElement.classList.remove(OVERSCROLL_LOCK_CLASS);
  document.body.classList.remove(OVERSCROLL_LOCK_CLASS);
  setNativeScrollEnabled(true);
}

function setNativeScrollEnabled(enabled: boolean) {
  try {
    (window as any).ReactNativeWebView?.postMessage(
      JSON.stringify({
        scrollEnabled: enabled,
      }),
    );
  } catch (error) {
    console.error('Failed to update native scroll state:', error);
  }
}

export function PdfViewer({ url, maxWidth, handleException }: Props) {
  const [numPages, setNumPages] = useState<number>();
  const [containerWidth, setContainerWidth] = useState<number>(maxWidth);

  const [scale, setScale] = useState<number>(1);
  const scaleRef = React.useRef(scale);
  const viewerRootRef = React.useRef<HTMLDivElement | null>(null);
  const scrollViewportRef = React.useRef<HTMLElement | null>(null);
  const pdfDocWrapRef = React.useRef<HTMLDivElement | null>(null);
  const pdfPagesRef = React.useRef<HTMLDivElement | null>(null);
  const zoomLabelRef = React.useRef<HTMLSpanElement | null>(null);
  const activePointersRef = React.useRef<Map<number, PointerPosition>>(new Map());
  const pinchStateRef = React.useRef<PinchState | null>(null);
  const pendingPinchScrollRef = React.useRef<PendingScroll | null>(null);
  const pendingVisualPinchFrameRef = React.useRef<VisualPinchFrame | null>(null);
  const pinchAnimationFrameRef = React.useRef<number | null>(null);
  const pendingVisualPinchClearRef = React.useRef(false);
  const visualPinchScaleRef = React.useRef<number | null>(null);
  const touchPanStateRef = React.useRef<TouchPanState | null>(null);
  const isPinchingRef = React.useRef(false);
  const suppressNextClickRef = React.useRef(false);

  // ...DRAGGING out of boundaries
  const draggingRef = React.useRef(false);

  const handleStartScroll = () => {
    if (isPinchingRef.current) {
      return;
    }

    draggingRef.current = true;
  };
  const handleEndScroll = () => {
    // keep true until the very next pointerup/click we handle
    // (don’t reset here; we’ll reset inside onPointerUp/onClick)
  };
  // END

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useLayoutEffect(() => {
    if (pendingVisualPinchClearRef.current) {
      clearVisualPinch();
      pendingVisualPinchClearRef.current = false;
    }

    applyPendingPinchScroll();
  }, [scale]);

  useEffect(() => {
    lockPageOverscroll();

    return unlockPageOverscroll;
  }, []);

  useEffect(() => {
    const viewerRoot = viewerRootRef.current;
    const viewport = scrollViewportRef.current;

    if (!viewerRoot || !viewport) {
      return;
    }

    function preventNativeTouchGesture(event: TouchEvent) {
      if (event.cancelable) {
        event.preventDefault();
      }
    }

    viewerRoot.addEventListener('touchmove', preventNativeTouchGesture, {
      passive: false,
      capture: true,
    });
    viewport.addEventListener('touchmove', preventNativeTouchGesture, { passive: false });

    return () => {
      viewerRoot.removeEventListener('touchmove', preventNativeTouchGesture, true);
      viewport.removeEventListener('touchmove', preventNativeTouchGesture);
    };
  }, []);

  useEffect(() => {
    function updateContainerWidth() {
      const vwWidth = window.innerWidth * 0.95;
      setContainerWidth(Math.min(vwWidth, maxWidth));
    }

    // Set initial width
    updateContainerWidth();

    // Add event listener to update width on window resize
    window.addEventListener('resize', updateContainerWidth);

    // Clean up event listener on component unmount
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, [maxWidth]);

  useEffect(() => {
    return () => clearVisualPinch();
  }, []);

  function onDocumentLoadSuccess({ numPages: nextNumPages }: PDFDocumentProxy): void {
    setNumPages(nextNumPages);
  }

  function handleError(error: Error) {
    console.log(error.message);
    handleException();
  }

  function zoomIn() {
    clearVisualPinch();
    setScale((s) => clampScale(s + BUTTON_SCALE_STEP));
  }
  function zoomOut() {
    clearVisualPinch();
    setScale((s) => clampScale(s - BUTTON_SCALE_STEP));
  }
  function zoomReset() {
    clearVisualPinch();
    setScale(1);
  }

  function getScaleLabel(nextScale: number): string {
    return `${Math.round(nextScale * 100)}%`;
  }

  function clampScroll(viewport: HTMLElement, scroll: PendingScroll): PendingScroll {
    return {
      left: Math.max(0, Math.min(scroll.left, viewport.scrollWidth - viewport.clientWidth)),
      top: Math.max(0, Math.min(scroll.top, viewport.scrollHeight - viewport.clientHeight)),
    };
  }

  function applyPendingPinchScroll() {
    const pendingScroll = pendingPinchScrollRef.current;
    const viewport = scrollViewportRef.current;

    if (!pendingScroll || !viewport) {
      return;
    }

    pendingPinchScrollRef.current = null;
    const clampedScroll = clampScroll(viewport, pendingScroll);
    viewport.scrollLeft = clampedScroll.left;
    viewport.scrollTop = clampedScroll.top;
  }

  function scheduleVisualPinch(frame: VisualPinchFrame) {
    pendingVisualPinchFrameRef.current = frame;

    if (pinchAnimationFrameRef.current !== null) {
      return;
    }

    pinchAnimationFrameRef.current = window.requestAnimationFrame(() => {
      pinchAnimationFrameRef.current = null;

      const nextFrame = pendingVisualPinchFrameRef.current;
      pendingVisualPinchFrameRef.current = null;

      if (!nextFrame) {
        return;
      }

      applyVisualPinch(nextFrame);
    });
  }

  function applyVisualPinch(frame: VisualPinchFrame) {
    const pinchState = pinchStateRef.current;
    const viewport = scrollViewportRef.current;
    const docWrap = pdfDocWrapRef.current;
    const pages = pdfPagesRef.current;

    if (!pinchState || !viewport || !docWrap || !pages) {
      return;
    }

    docWrap.style.width = `${Math.max(1, pinchState.startContentWidth * frame.scaleRatio)}px`;
    docWrap.style.height = `${Math.max(1, pinchState.startContentHeight * frame.scaleRatio)}px`;

    pages.style.transformOrigin = 'top left';
    pages.style.transform = `scale(${frame.scaleRatio})`;
    pages.style.willChange = 'transform';

    if (zoomLabelRef.current) {
      zoomLabelRef.current.textContent = getScaleLabel(frame.displayScale);
    }

    const clampedScroll = clampScroll(viewport, frame);
    viewport.scrollLeft = clampedScroll.left;
    viewport.scrollTop = clampedScroll.top;
  }

  function clearVisualPinch() {
    if (pinchAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(pinchAnimationFrameRef.current);
      pinchAnimationFrameRef.current = null;
    }

    pendingVisualPinchFrameRef.current = null;

    const docWrap = pdfDocWrapRef.current;
    const pages = pdfPagesRef.current;

    if (docWrap) {
      docWrap.style.removeProperty('width');
      docWrap.style.removeProperty('height');
    }

    if (pages) {
      pages.style.removeProperty('transform');
      pages.style.removeProperty('transform-origin');
      pages.style.removeProperty('will-change');
    }

    if (zoomLabelRef.current) {
      zoomLabelRef.current.textContent = getScaleLabel(scaleRef.current);
    }
  }

  function getTouchPointers(): PointerPosition[] {
    return Array.from(activePointersRef.current.values()).slice(0, 2);
  }

  function beginPinch() {
    const [first, second] = getTouchPointers();
    const viewport = scrollViewportRef.current;
    const docWrap = pdfDocWrapRef.current;

    if (!first || !second || !viewport || !docWrap) {
      return;
    }

    const viewportRect = viewport.getBoundingClientRect();
    const contentRect = docWrap.getBoundingClientRect();
    const midpoint = getMidpoint(first, second);

    isPinchingRef.current = true;
    draggingRef.current = false;
    touchPanStateRef.current = null;
    pinchStateRef.current = {
      startDistance: getDistance(first, second),
      startMidpointX: midpoint.clientX - viewportRect.left,
      startMidpointY: midpoint.clientY - viewportRect.top,
      startScale: scaleRef.current,
      startScrollLeft: viewport.scrollLeft,
      startScrollTop: viewport.scrollTop,
      startContentWidth: contentRect.width,
      startContentHeight: contentRect.height,
    };
  }

  function beginTouchPan(event: React.PointerEvent<HTMLDivElement>) {
    const viewport = scrollViewportRef.current;

    if (!viewport) {
      return;
    }

    touchPanStateRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startScrollLeft: viewport.scrollLeft,
      startScrollTop: viewport.scrollTop,
      hasMoved: false,
    };
  }

  function updateTouchPan(event: React.PointerEvent<HTMLDivElement>) {
    const panState = touchPanStateRef.current;
    const viewport = scrollViewportRef.current;

    if (
      !panState ||
      !viewport ||
      panState.pointerId !== event.pointerId ||
      activePointersRef.current.size !== 1
    ) {
      return;
    }

    const deltaX = event.clientX - panState.startClientX;
    const deltaY = event.clientY - panState.startClientY;

    if (!panState.hasMoved && Math.hypot(deltaX, deltaY) < TOUCH_PAN_ACTIVATION_DISTANCE) {
      return;
    }

    panState.hasMoved = true;
    draggingRef.current = true;
    event.preventDefault();
    event.stopPropagation();

    viewport.scrollLeft = panState.startScrollLeft - deltaX;
    viewport.scrollTop = panState.startScrollTop - deltaY;
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!activePointersRef.current.has(event.pointerId)) {
      return;
    }

    activePointersRef.current.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
    });

    if (activePointersRef.current.size < 2) {
      updateTouchPan(event);
      return;
    }

    if (!pinchStateRef.current) {
      beginPinch();
    }

    const pinchState = pinchStateRef.current;
    const viewport = scrollViewportRef.current;
    const [first, second] = getTouchPointers();

    if (!pinchState || !viewport || !first || !second || pinchState.startDistance <= 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const viewportRect = viewport.getBoundingClientRect();
    const midpoint = getMidpoint(first, second);
    const nextScale = clampScale(
      (pinchState.startScale * getDistance(first, second)) / pinchState.startDistance,
    );
    const scaleRatio = nextScale / pinchState.startScale;

    const pendingScroll = {
      left:
        (pinchState.startScrollLeft + pinchState.startMidpointX) * scaleRatio -
        (midpoint.clientX - viewportRect.left),
      top:
        (pinchState.startScrollTop + pinchState.startMidpointY) * scaleRatio -
        (midpoint.clientY - viewportRect.top),
    };

    pendingPinchScrollRef.current = pendingScroll;
    visualPinchScaleRef.current = nextScale;

    scheduleVisualPinch({
      ...pendingScroll,
      scaleRatio,
      displayScale: nextScale,
    });
  }

  function commitVisualPinch() {
    const nextScale = visualPinchScaleRef.current;
    visualPinchScaleRef.current = null;

    if (nextScale === null || Math.abs(nextScale - scaleRef.current) < 0.001) {
      clearVisualPinch();
      applyPendingPinchScroll();
      return;
    }

    pendingVisualPinchClearRef.current = true;
    scaleRef.current = nextScale;
    setScale(nextScale);
  }

  function endPointer(event: React.PointerEvent<HTMLDivElement>) {
    if (!activePointersRef.current.has(event.pointerId)) {
      return;
    }

    activePointersRef.current.delete(event.pointerId);

    if (touchPanStateRef.current?.pointerId === event.pointerId) {
      if (touchPanStateRef.current.hasMoved) {
        event.preventDefault();
        event.stopPropagation();
        suppressNextClickRef.current = true;
      }

      touchPanStateRef.current = null;
    }

    if (!isPinchingRef.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    draggingRef.current = false;
    suppressNextClickRef.current = true;

    pinchStateRef.current = null;
    isPinchingRef.current = false;
    commitVisualPinch();

    if (activePointersRef.current.size >= 2) {
      beginPinch();
    }
  }

  const pageWidth = Math.floor(containerWidth * scale);

  return (
    // Root: column layout with a bounded height (acts as the viewport)
    <div ref={viewerRootRef} className="pdf-viewer-root">
      {/* Toolbar OUTSIDE the scrollable region */}
      <div className="pdf-viewer-toolbar" style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <button className="btn btn-primary" onClick={zoomOut} aria-label="Zoom out">
          −
        </button>
        <button className="btn btn-primary" onClick={zoomReset} aria-label="Reset zoom">
          100%
        </button>
        <button className="btn btn-primary" onClick={zoomIn} aria-label="Zoom in">
          +
        </button>
        <span
          ref={zoomLabelRef}
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center' }}
        >
          {getScaleLabel(scale)}
        </span>
      </div>

      {/* Scrollable region consumes remaining space */}
      <ScrollContainer
        className="pdf-viewer-documents"
        innerRef={scrollViewportRef}
        vertical
        horizontal
        hideScrollbars={false}
        onStartScroll={handleStartScroll}
        onEndScroll={handleEndScroll}
        style={{
          flex: '1 1 0',
          minHeight: 0, // <- crucial in flex/grid to allow overflow instead of expanding
          width: '100%',
          // height: '100%',     // optional; not needed when using flex + minHeight: 0
        }}
      >
        <div
          className="pdf-scroll-track"
          onPointerDown={(e) => {
            if (e.pointerType === 'touch') {
              activePointersRef.current.set(e.pointerId, {
                clientX: e.clientX,
                clientY: e.clientY,
              });

              if (activePointersRef.current.size === 1) {
                beginTouchPan(e);
              } else {
                beginPinch();
                e.preventDefault();
                e.stopPropagation();
              }
            }

            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          }}
          onPointerMove={handlePointerMove}
          onPointerUp={(e) => {
            const wasPinching = isPinchingRef.current;
            endPointer(e);

            if (wasPinching || draggingRef.current) {
              e.preventDefault();
              e.stopPropagation();
              draggingRef.current = false; // reset after swallowing the up
            }
          }}
          onPointerCancel={endPointer}
          onLostPointerCapture={endPointer}
          onClick={(e) => {
            if (suppressNextClickRef.current || draggingRef.current) {
              e.preventDefault();
              e.stopPropagation();
              suppressNextClickRef.current = false;
              draggingRef.current = false; // safety reset
            }
          }}
        >
          <div className="pdf-doc-wrap" ref={pdfDocWrapRef}>
            <div className="pdf-pages" ref={pdfPagesRef}>
              <Document
                file={url}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={handleError}
                options={options}
                loading={<i className="fas fa-spinner fa-spin fa-2x pdf-loading-spinner" />}
                error={'Prišlo je do napake'}
                onPassword={(callback, reason) => {
                  function callbackProxy(password: string | null) {
                    if (password === null) {
                      handleException();
                      return;
                    }
                    callback(password);
                  }
                  switch (reason) {
                    case PasswordResponses.NEED_PASSWORD: {
                      const password = prompt('Vnesite geslo');
                      callbackProxy(password);
                      break;
                    }
                    case PasswordResponses.INCORRECT_PASSWORD: {
                      const password = prompt('Napačno geslo');
                      callbackProxy(password);
                      break;
                    }
                    default:
                  }
                }}
              >
                {Array.from(new Array(numPages), (_el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={pageWidth} // responsive fit…
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                ))}
              </Document>
            </div>
          </div>
        </div>
      </ScrollContainer>
    </div>
  );
}
